import React, { lazy, Suspense, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { BrowserRouter, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import {
  SignedIn,
  SignedOut,
  AuthenticateWithRedirectCallback,
  useClerk,
} from '@clerk/clerk-react';

import IndexEntry from './DashboardEntry.jsx';
import Layout from './Layout.js';
import SignUpPage from './SignUpPage.jsx';
import SignInPage from './SignInPage.jsx';
import ForgotPasswordPage from './ForgotPasswordPage.jsx';
import UnsubPage from './UnsubPage.jsx';
import RoadmapEmailActionPage from './RoadmapEmailActionPage.jsx';
import InterestedPage from './InterestedPage.jsx';
import NotFoundPage from './NotFoundPage.jsx';
import Reroute from './Reroute.jsx';
import PublicLeaderboard from './PublicLeaderboardPage.jsx';
import { AppProvider } from './AppContext.js';
import { ImpersonationProvider } from './contexts/ImpersonationContext.jsx';
import AdminViewAsControl from './components/AdminViewAsControl.jsx';
import Automate from './automate-my-work/automate.jsx';
import PublicLeaderboardStaging from './PublicLeaderboardPage_Staging.jsx';
import ClientSuccessWallPage from './ClientSuccessWallPage.jsx';
import HomeEntry from './HomeEntry.jsx';
import AppChooser from './components/AppChooser.jsx';
import { publicMarketingRoutes, signedInMarketingRoutes } from './publicMarketingRoutes.jsx';
import EmailPrototypePage from './email-prototype/EmailPrototypePage.jsx';
import TeamAdminRoute from './components/TeamAdminRoute.jsx';
import RouteAnalytics from './components/RouteAnalytics.jsx';
// import UsersAdminPage from './UsersAdminPage.js';

// Ensure all lazy-loaded components are properly wrapped with error handling
const safeImport = (importFn) => {
  return () => importFn().catch((error) => {
    console.error("Error loading component:", error);
    throw error;
  });
};

// Update lazy loading with error handling
const CompaniesPage = lazy(safeImport(() => import('./CompaniesPage.jsx')));
const Leaderboard = lazy(safeImport(() => import('./LeaderboardPage.jsx')));
const ProspectLeaderboard = lazy(safeImport(() => import('./ProspectLeaderboardPage.jsx')));
const EditGrantPage = lazy(safeImport(() => import('./EditGrantPage.jsx')));
const FeedBackPage = lazy(safeImport(() => import('./FeedbackPage.jsx')));
const AllGrants = lazy(safeImport(() => import('./AllGrants.jsx')));
const GrantAnswersPage = lazy(safeImport(() => import('./GrantAnswersPage.jsx')));
const EnhancedGrantAnswersPage = lazy(safeImport(() => import('./EnhancedGrantAnswersPage.jsx')));
const CompanyInfoPage = lazy(safeImport(() => import('./CompanyInfoPage.jsx')));
const CheckOAuthGoogle = lazy(safeImport(() => import('./CheckOAuthGoogle.jsx')));
const WorkspaceSelectionPage = lazy(safeImport(() => import('./WorkspaceSelectionPage.jsx')));
const MyNetwork = lazy(safeImport(() => import('./MyNetwork.js')));
const GrantWriterNetwork = lazy(safeImport(() => import('./GrantWriterNetwork.js')));
const Perks = lazy(safeImport(() => import('./Perks.js')));
const PlansPage = lazy(safeImport(() => import('./PlansPage.jsx')));
const FinancingRequests = lazy(safeImport(() => import('./FinancingRequests.jsx')));
const AutomationRequests = lazy(safeImport(() => import('./AutomationRequests.jsx')));
const AutomationSuccess = lazy(safeImport(() => import('./AutomationSuccess.jsx')));
const UpgraderSubscriptions = lazy(safeImport(() => import('./components/UpgraderSubscriptions.jsx')));
const AllOpportunitiesBoard = lazy(safeImport(() => import('./AllOpportunitiesBoard.jsx')));
const UsersAdmin = lazy(safeImport(() => import('./UsersAdminPage.js')));
const CustomersAdmin = lazy(safeImport(() => import('./CustomersAdminPage.jsx')));
const VendorSavingsAdmin = lazy(safeImport(() => import('./VendorSavingsAdminPage.js')));
const DataCleanUp = lazy(safeImport(() => import('./components/DataCleanUp.jsx')));
const CompanyInfoPageRoute = lazy(safeImport(() => import('./pages/CompanyInfoPageRoute.jsx')));
const MyReportsPage = lazy(safeImport(() => import('./pages/MyReportsPage.jsx')));
const UpgradesPage = lazy(safeImport(() => import('./pages/UpgradesPage.jsx')));
const SoftwareSavingsPage = lazy(safeImport(() => import('./pages/SoftwareSavingsPage.jsx')));
const SoftwareSavingsDetailPage = lazy(safeImport(() => import('./pages/SoftwareSavingsDetailPage.jsx')));
const RevenueUpgradesPage = lazy(safeImport(() => import('./pages/RevenueUpgradesPage.jsx')));
const AIAutomationsPage = lazy(safeImport(() => import('./pages/AIAutomationsPage.jsx')));
const FinancingPage = lazy(safeImport(() => import('./pages/FinancingPage.jsx')));
const FinancingPartnersPage = lazy(safeImport(() => import('./pages/FinancingPartnersPage.jsx')));
const OnboardingPageRoute = lazy(safeImport(() => import('./pages/OnboardingPageRoute.jsx')));
const OnboardingPage = lazy(safeImport(() => import('./OnboardingPage.jsx')));
const OnboardingV2Page = lazy(safeImport(() => import('./OnboardingV2Page.jsx')));
const OnboardingPaymentPage = lazy(safeImport(() => import('./OnboardingPaymentPage.jsx')));
const HomePage = lazy(safeImport(() => import('./pages/HomePage.jsx')));
const GrantsReportPage = lazy(safeImport(() => import('./pages/GrantsReportPage.jsx')));
const PricingPageNew = lazy(safeImport(() => import('./pages/PricingPageNew.jsx')));
const TeamDocsIndexPage = lazy(safeImport(() => import('./pages/TeamDocsIndexPage.jsx')));
const TeamDocPage = lazy(safeImport(() => import('./pages/TeamDocPage.jsx')));
const SredClaimPage = lazy(safeImport(() => import('./pages/SredClaimPage.jsx')));
const ArLedgerLogPage = lazy(safeImport(() => import('./pages/ArLedgerLogPage.jsx')));
const GrantPublicPage = lazy(safeImport(() => import('./pages/GrantPublicPage.jsx')));
const RdAllocationPage = lazy(safeImport(() => import('./pages/RdAllocationPage.jsx')));

/** Preserve query params when redirecting legacy /settings → Profile & Company. */
function SettingsRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  // Map old tab credits success to panel=plans
  if (params.get('credits') === 'success' || params.get('credits') === 'canceled') {
    // keep credits param; ProfileCompanySettings handles it
  } else if (!params.get('panel') && location.hash) {
    // no-op
  }
  return <Navigate to={`/company-info-section${location.search}`} replace />;
}

function SSOCallback() {
  const { handleRedirectCallback } = useClerk();
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const processCallback = async () => {
      try {
        // Grant page sign-in: route through check_oauth_google first so user gets created in Airtable,
        // then redirect to the grant page. (Direct redirect bypassed CheckOAuthGoogle = users never created)
        const grantPageRedirect = sessionStorage.getItem("grantPageRedirectAfterSignIn");
        if (grantPageRedirect) {
          sessionStorage.removeItem("grantPageRedirectAfterSignIn");
          sessionStorage.setItem("grantPageRedirectAfterOAuth", grantPageRedirect);
          const redirectUrl = `/check_oauth_google?redirect=${encodeURIComponent(grantPageRedirect)}`;
          await handleRedirectCallback({ redirectUrl });
          return;
        }
        const freeSignupFlag = sessionStorage.getItem("freeSignup") === "true";
        const redirectUrl = freeSignupFlag
          ? "/check_oauth_google?onboarding=true"
          : "/check_oauth_google";
        await handleRedirectCallback({ redirectUrl });
      } catch (error) {
        if (cancelled) return;
        console.error("Error handling SSO callback:", error);
        // "No sign up attempt was found" - existing user used sign-up OAuth; redirect to sign-in
        const isNoSignUpAttempt = error?.message?.toLowerCase().includes('no sign up attempt') ||
          error?.errors?.[0]?.message?.toLowerCase().includes('no sign up attempt');
        if (isNoSignUpAttempt) {
          navigate("/sign-in?error=already_exists");
        } else {
          // On other errors, still try check_oauth_google - user may be signed in
          navigate("/check_oauth_google");
        }
      }
    };
    processCallback();
    return () => { cancelled = true; };
  }, [handleRedirectCallback, navigate]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm text-center">
        <div className="loading-spinner mx-auto mb-6" />
        <h1 className="text-lg font-semibold text-gray-900 mb-2">
          Completing sign in
        </h1>
        <p className="text-sm text-gray-500 mb-6">
          Please wait while we finish authenticating your account. You’ll be redirected in a moment.
        </p>
        <div className="h-1 w-full bg-gray-200 rounded-full overflow-hidden">
          <div className="h-full w-full bg-indigo-500 rounded-full animate-pulse" />
        </div>
      </div>
    </div>
  );
}

// Loading fallback component - lightweight CSS spinner instead of heavy component
const LoadingFallback = () => (
  <div className="loading-spinner-container">
    <div className="loading-spinner"></div>
  </div>
);

const Router = () => {
  useEffect(() => {
    //"fetch everything from the router and pass as props to child components"//
  }, []);

  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <RouteAnalytics />
      <AppProvider>
        <ImpersonationProvider>
        <SignedOut>
          <Routes>
            <Route path="/public-leaderboard" element={<PublicLeaderboardStaging/>} />
            <Route path="/public-leaderboard-staging" element={<PublicLeaderboardStaging />} />
            <Route path="/wall-of-client-success" element={<ClientSuccessWallPage />} />
            <Route path="/automate-my-work" element={<Automate />} />
            <Route path="/sign-up" element={<AppChooser mode="sign-up"><SignUpPage /></AppChooser>} />
            <Route path="/free-signup" element={<SignUpPage isFreeSignup={true} />} />
            <Route path="/sign-in" element={<AppChooser mode="sign-in"><SignInPage /></AppChooser>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/unsubscribe" element={<UnsubPage />} />
            <Route path="/roadmap-action" element={<RoadmapEmailActionPage />} />
            <Route path="/api/partner-interested" element={<InterestedPage />} />
            <Route path="/sso-callback" element={<SSOCallback />} />
            <Route path="/workspace-selection" element={<Reroute />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/onboarding-v2" element={<OnboardingV2Page />} />
            <Route path="/company-earnings-estimate" element={<OnboardingV2Page />} />
            <Route path="/onboarding-payment" element={<OnboardingPaymentPage />} />
            <Route path="/pricing-new" element={<Suspense fallback={<div className="loading-spinner-container"><div className="loading-spinner"></div></div>}><PricingPageNew /></Suspense>} />
            <Route path="/grants-form/*" element={<GrantAnswersPage />} />
            <Route path="/enhanced-grants-form/*" element={<EnhancedGrantAnswersPage />} />
            <Route path="/users-admin" element={<UsersAdmin />} />
            <Route path="/customers-admin" element={<CustomersAdmin />} />
            <Route path="/vendor-savings-admin" element={<VendorSavingsAdmin />} />
            <Route path="/software-savings/:slug" element={<Suspense fallback={<div className="loading-spinner-container"><div className="loading-spinner"></div></div>}><SoftwareSavingsDetailPage /></Suspense>} />
            <Route path="/financing-partners" element={<Suspense fallback={<LoadingFallback />}><FinancingPartnersPage /></Suspense>} />
            {publicMarketingRoutes}
            <Route
              path="/grant/:slug"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <GrantPublicPage />
                </Suspense>
              }
            />
            <Route
              path="/rd-allocation"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RdAllocationPage />
                </Suspense>
              }
            />
            <Route
              path="/rd-allocation/:companyId"
              element={
                <Suspense fallback={<LoadingFallback />}>
                  <RdAllocationPage />
                </Suspense>
              }
            />

            <Route path="/" element={<Layout />}>
              <Route index element={<HomeEntry />} />
              <Route path="all-grants" element={<Suspense fallback={<div className="loading-spinner-container"><div className="loading-spinner"></div></div>}><AllGrants /></Suspense>} />
              <Route path="grants-section" element={<Suspense fallback={<div className="loading-spinner-container"><div className="loading-spinner"></div></div>}><GrantsReportPage /></Suspense>} />
            </Route>

            <Route path="/*" element={<Reroute />} />
          </Routes>
        </SignedOut>
        <SignedIn>
          <AdminViewAsControl />
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/public-leaderboard-staging" element={<PublicLeaderboardStaging />} />
              <Route path="/public-leaderboard" element={<PublicLeaderboardStaging/>} />
              <Route path="/wall-of-client-success" element={<ClientSuccessWallPage />} />
              <Route path="/automate-my-work" element={<Automate />} />
              <Route path="/report-pending" element={<FeedBackPage />} />
              <Route path="/company-info/*" element={<CompanyInfoPage />} />
              <Route path="/unsubscribe" element={<UnsubPage />} />
              <Route path="/roadmap-action" element={<RoadmapEmailActionPage />} />
              <Route
                path="/api/partner-interested"
                element={<InterestedPage />}
              />
              <Route path="/grants-form/*" element={<GrantAnswersPage />} />
              <Route path="/sign-in" element={<Reroute />} />
              <Route path="/sign-up" element={<Reroute />} />
              <Route path="/free-signup" element={<Reroute />} />
              <Route path="/forgot-password" element={<Reroute />} />
              <Route path="/check_oauth_google" element={<CheckOAuthGoogle />} />
              <Route path="/workspace-selection" element={<WorkspaceSelectionPage />} />
              <Route path="/upgrader-subscriptions" element={<UpgraderSubscriptions />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/onboarding-v2" element={<OnboardingV2Page />} />
              <Route path="/company-earnings-estimate" element={<OnboardingV2Page />} />
              <Route path="/onboarding-payment" element={<OnboardingPaymentPage />} />
              <Route path="/software-savings/:slug" element={<Suspense fallback={<div className="loading-spinner-container"><div className="loading-spinner"></div></div>}><SoftwareSavingsDetailPage /></Suspense>} />
              <Route path="/financing-partners" element={<Suspense fallback={<LoadingFallback />}><FinancingPartnersPage /></Suspense>} />
              {signedInMarketingRoutes}
              <Route
                path="/grant/:slug"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <GrantPublicPage />
                  </Suspense>
                }
              />
              <Route
                path="/rd-allocation"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <RdAllocationPage />
                  </Suspense>
                }
              />
              <Route
                path="/rd-allocation/:companyId"
                element={
                  <Suspense fallback={<LoadingFallback />}>
                    <RdAllocationPage />
                  </Suspense>
                }
              />

              <Route path="/" element={<Layout />}>
                <Route index element={<IndexEntry />} />
                <Route path="enhanced-grants-form/*" element={<EnhancedGrantAnswersPage />} />
                <Route path="home" element={<Navigate to="/" replace />} />
                <Route path="edit-grant-questions/:grantId?" element={<EditGrantPage />} />
                <Route path="automation-success" element={<AutomationSuccess />} />
                <Route path="all-grants" element={<AllGrants />} />
                <Route path="companies" element={<CompaniesPage />} />
                <Route path="leaderboard" element={<Leaderboard />} />
                <Route path="prospect-leaderboard" element={<ProspectLeaderboard />} />
                <Route path="my-network" element={<MyNetwork />} />
                <Route path="successful-grant-writers" element={<GrantWriterNetwork />} />
                <Route path="perks" element={<Perks />} />
                <Route path="pricing" element={<PlansPage />} />
                <Route path="pricing-new" element={<PricingPageNew />} />
                <Route path="financing-requests" element={<FinancingRequests />} />
                <Route path="automation-requests" element={<AutomationRequests />} />
                <Route path="settings" element={<SettingsRedirect />} />
                <Route path="email-prototype" element={<EmailPrototypePage />} />
                <Route
                  path="all-opportunities"
                  element={
                    <TeamAdminRoute>
                      <AllOpportunitiesBoard />
                    </TeamAdminRoute>
                  }
                />
                <Route path="users-admin" element={<UsersAdmin />} />
                <Route path="customers-admin" element={<CustomersAdmin />} />
                <Route path="vendor-savings-admin" element={<VendorSavingsAdmin />} />
                <Route path="grants-admin" element={<DataCleanUp />} />
                <Route path="industry-matching" element={<DataCleanUp />} />
                <Route path="data-cleanup" element={<DataCleanUp />} />
                <Route path="perfectdraft-data-cleanup" element={<DataCleanUp />} />
                <Route path="company-info-section" element={<CompanyInfoPageRoute />} />
                <Route path="financing" element={<FinancingPage />} />
                <Route path="my-reports" element={<MyReportsPage />} />
                <Route path="software-savings" element={<SoftwareSavingsPage />} />
                <Route path="grants-section" element={<GrantsReportPage />} />
                <Route path="revenue-upgrades" element={<RevenueUpgradesPage />} />
                <Route path="ai-automations" element={<AIAutomationsPage />} />
                <Route path="onboarding-section" element={<OnboardingPageRoute />} />
                <Route path="admin/team-docs" element={<TeamDocsIndexPage />} />
                <Route path="admin/team-docs/:slug" element={<TeamDocPage />} />
                <Route
                  path="admin/ar-ledger-log"
                  element={
                    <TeamAdminRoute>
                      <ArLedgerLogPage />
                    </TeamAdminRoute>
                  }
                />
                <Route
                  path="sred"
                  element={
                    <TeamAdminRoute>
                      <SredClaimPage />
                    </TeamAdminRoute>
                  }
                />
                <Route
                  path="sred/:claimId"
                  element={
                    <TeamAdminRoute>
                      <SredClaimPage />
                    </TeamAdminRoute>
                  }
                />
                <Route
                  path="admin/earnings-roadmap-email"
                  element={<Navigate to="/admin/team-docs/earnings-roadmap-email" replace />}
                />
                <Route path="*" element={<NotFoundPage />} />
              </Route>
            </Routes>
          </Suspense>
        </SignedIn>
        </ImpersonationProvider>
      </AppProvider>
    </BrowserRouter>
  );
};

export default Router;